import * as React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileText, ChevronDown, Loader2, Sun, Moon } from "lucide-react";
import { exportData, type ExportFormat, type ExportHeader, type ExportOptions } from "@/lib/export-utils";
import { cn } from "@/lib/utils";

export interface ExportDropdownButtonProps<T extends Record<string, unknown>> {
  /** Data to export */
  data: T[];
  /** Column headers configuration */
  headers: ExportHeader[];
  /** Base filename (without extension) */
  filename: string;
  /** Optional PDF export options */
  pdfOptions?: ExportOptions;
  /** Button label */
  label?: string;
  /** Button variant */
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  /** Button size */
  size?: "default" | "sm" | "lg" | "icon";
  /** Additional className for the button */
  className?: string;
  /** Additional className for the dropdown menu content */
  contentClassName?: string;
  /** Disable the button */
  disabled?: boolean;
  /** Custom export handler - if provided, overrides default behavior. Can be async. */
  onExport?: (format: ExportFormat) => void | Promise<void>;
  /** Callback after successful export */
  onExportComplete?: (format: ExportFormat) => void;
  /** When true, the PDF menu item becomes a sub-menu with Light and Dark theme options */
  showPdfThemeOptions?: boolean;
}

export function ExportDropdownButton<T extends Record<string, unknown>>({
  data,
  headers,
  filename,
  pdfOptions,
  label = "Export",
  variant = "outline",
  size = "default",
  className,
  contentClassName,
  disabled = false,
  onExport,
  onExportComplete,
  showPdfThemeOptions = false,
}: ExportDropdownButtonProps<T>) {
  const [isExporting, setIsExporting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true);
    setDropdownOpen(false);
    
    try {
      // Use setTimeout to allow UI to update before potentially heavy export operation
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (onExport) {
        await onExport(format);
      } else {
        exportData(format, data, headers, filename, pdfOptions);
      }
      onExportComplete?.(format);
    } finally {
      setIsExporting(false);
    }
  };

  const iconSize = size === "sm" ? "h-3 w-3" : "h-4 w-4";

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          className={cn("gap-1", className)}
          disabled={disabled || data.length === 0 || isExporting}
        >
          {isExporting ? (
            <Loader2 className={cn(iconSize, "animate-spin")} />
          ) : (
            <Download className={iconSize} />
          )}
          <span>{isExporting ? "Exporting..." : label}</span>
          {!isExporting && <ChevronDown className={cn(iconSize, "opacity-50")} />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn("filter-popout-glow w-40", contentClassName)}>
        {showPdfThemeOptions ? (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="cursor-pointer">
              <FileText className="h-4 w-4 mr-2 text-red-500" />
              PDF
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="filter-popout-glow w-36">
              <DropdownMenuItem
                onClick={() => handleExport('pdf-light')}
                className="cursor-pointer"
              >
                <Sun className="h-4 w-4 mr-2 text-yellow-500" />
                Light Mode
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleExport('pdf-dark')}
                className="cursor-pointer"
              >
                <Moon className="h-4 w-4 mr-2 text-indigo-400" />
                Dark Mode
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ) : (
          <DropdownMenuItem
            onClick={() => handleExport('pdf')}
            className="cursor-pointer"
          >
            <FileText className="h-4 w-4 mr-2 text-red-500" />
            PDF
          </DropdownMenuItem>
        )}
        <DropdownMenuItem 
          onClick={() => handleExport('excel')}
          className="cursor-pointer"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
          Excel
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleExport('csv')}
          className="cursor-pointer"
        >
          <FileText className="h-4 w-4 mr-2 text-blue-500" />
          CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { type ExportFormat, type ExportHeader, type ExportOptions } from "@/lib/export-utils";

